/**
 * Cost item resolution — the one authoritative cost pipeline (plan §5.4, §5.5; PP §7.2).
 *
 * The rule the product plan insists on is "do not sum the full original estimate with actuals": a cost
 * item contributes **one** figure, chosen by its status, and the category total replaces the equation's
 * estimate only when that category actually has items. A category that has none keeps whatever the
 * equation worked out, which is how a fee that nobody has booked yet still appears in the projection.
 *
 * Holding is deliberately not resolvable: it has no cost category, it is produced by the `holding`
 * equation alone, and it is therefore accounted exactly once (plan §3.3).
 */
import { sum } from '../money.js';
const nonNegative = (value) => (value === null || value === undefined ? 0 : value);
/** The §5.5 table, item by item. */
export const resolveCostItem = (item) => {
    const base = { id: item.id, name: item.name, category: item.category, status: item.status };
    switch (item.status) {
        case 'ESTIMATED':
            return { ...base, amount: nonNegative(item.estimate), basis: 'ESTIMATE', provisional: true };
        case 'IN_PROGRESS': {
            const remaining = item.remainingEstimate ?? item.estimate;
            return {
                ...base,
                amount: sum([nonNegative(item.actualToDate), nonNegative(remaining)]),
                basis: 'ACTUAL_PLUS_REMAINING',
                provisional: true,
            };
        }
        case 'DONE':
            // A DONE item without an actual is a data gap, not a free repair: fall back to the estimate and
            // keep the total flagged provisional so nobody reads it as a settled figure.
            return item.actualToDate === null || item.actualToDate === undefined
                ? { ...base, amount: nonNegative(item.estimate), basis: 'ESTIMATE', provisional: true }
                : { ...base, amount: item.actualToDate, basis: 'ACTUAL', provisional: false };
        case 'WAIVED':
            return { ...base, amount: 0, basis: 'WAIVED', provisional: false };
        default:
            return { ...base, amount: nonNegative(item.estimate), basis: 'ESTIMATE', provisional: true };
    }
};
/** Groups the resolved items by category. Categories with no items are absent from the result. */
export const resolveCosts = (items) => {
    const resolutions = {};
    for (const item of items) {
        const resolved = resolveCostItem(item);
        const existing = resolutions[item.category];
        if (existing) {
            existing.items.push(resolved);
            existing.amount = sum(existing.items.map((entry) => entry.amount));
            existing.provisional = existing.provisional || resolved.provisional;
            continue;
        }
        resolutions[item.category] = {
            category: item.category,
            amount: resolved.amount,
            items: [resolved],
            provisional: resolved.provisional,
        };
    }
    return resolutions;
};
/** What the `repair` equation reads as `reconditioningResolved` (plan §5.3). */
export const resolvedReconditioning = (resolutions) => resolutions.RECONDITIONING?.amount ?? 0;
export const isProvisional = (resolutions) => Object.values(resolutions).some((resolution) => resolution?.provisional === true);
//# sourceMappingURL=resolution.js.map